import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
  InputAdornment,
  Grid,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken, getDecryptedData, getDecryptedUserType } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import { useParams } from "react-router-dom";

const UpdatePatientDocumentsDialog = () => {
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const accessToken = getDecryptedAccessToken();
   const LoginUserDetails = getDecryptedData();
    const LoginUserType = getDecryptedUserType();
  const params = useParams()
    const pid =
      LoginUserType === "Patient" ? LoginUserDetails?.pid : params?.pid;

      useEffect(() => {
        if (!pid) return;
      
        const apiUrl =
          LoginUserType === "Patient"
            ? `/apis/default/portal/documents`
            : `/apis/default/api/documents`;
      
        ApiServices.getwitParams(
          apiUrl,
          { pid: pid },
          {
            Authorization: `Bearer ${accessToken}`,
          }
        )
          .then((response) => {
            setDocuments(response.data.documents || []);
          })
          .catch((error) => {
            console.error("Error fetching patient documents:", error);
            setDocuments([]);
          })
          .finally(() => setLoading(false));
      }, [pid, LoginUserType, accessToken]);
      

  const filteredDocs = documents?.filter((doc) =>
    doc.document_type.toLowerCase().includes(search.toLowerCase())
  );

  const handlePreview = (filePath) => {
    const fileUrl = filePath.replace("/var/www/html", "http://18.117.9.252");
    setSelectedFile(fileUrl);
    setOpenPreview(true);
  };

  const handleUpload = async () => {
    if (!file || !documentType) {
      setSnackbarMessage("Please select a file and enter a document name.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = async () => {
      const base64File = reader.result;

      const payload = {
        pid: pid,
        documentType: documentType,
        file: base64File,
      };

      try {
        const response = await ApiServices.post(
          `/apis/default/portal/documents`,
          payload,
          {
            Authorization: `Bearer ${accessToken}`,
          }
        );

        if (response.data.status === "success") {
          setSnackbarMessage("File uploaded successfully!");
          setSnackbarSeverity("success");
        } else {
          setSnackbarMessage("Failed to upload file.");
          setSnackbarSeverity("error");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setSnackbarMessage("An error occurred. Please try again.");
        setSnackbarSeverity("error");
      } finally {
        setLoading(false);
        setSnackbarOpen(true);
      }
    };
  };

  return (
    <>
       
        <Box sx={{ mb: 3, p: 2, border: "2px dashed #028391", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "#028391", mb: 2, fontWeight: "bold" }}>Upload New Document</Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Document Name"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            sx={{ mb: 2 }}
          />
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ marginBottom: "10px" }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              variant="contained"
              sx={{ backgroundColor: "#028391", color: "#fff", "&:hover": { backgroundColor: "#026c74" } }}
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Upload"}
            </Button>
            {file && (
              <Typography variant="body2" color="gray">
                Selected: {file.name}
              </Typography>
            )}
          </Box>
        </Box>

        <TextField
          fullWidth
          placeholder="Search by document type..."
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />
        {/* Document List */}
        <Grid container spacing={2}>
          {filteredDocs?.length > 0 ? (
            filteredDocs.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    border: "2px solid #028391",
                  }}
                  onClick={() => handlePreview(doc.file_path)}
                >
                  <DescriptionIcon sx={{ fontSize: 40, color: "#028391", mr: 2 }} />
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" color="#028391">
                      {doc.document_type}
                    </Typography>
                    <Typography variant="body2" color="gray">
                      Uploaded: {new Date(doc.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography color="gray">No documents found.</Typography>
          )}
        </Grid>
   

      {/* Document Preview Dialog */}
      {selectedFile && (
        <DocumentPreviewDialog open={openPreview} fileUrl={selectedFile} handleClose={() => setOpenPreview(false)} />
      )}

      {/* Snackbar for Notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </>
  );
};

export default UpdatePatientDocumentsDialog;
// import React, { useState } from "react";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   TextField,
//   IconButton,
//   Box,
//   Typography,
//   Card,
//   CardContent,
//   InputAdornment,
//   Grid,
//   Button,
//   CircularProgress,
//   Snackbar,
//   Alert,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import SearchIcon from "@mui/icons-material/Search";
// import DescriptionIcon from "@mui/icons-material/Description";
// import DocumentPreviewDialog from "./DocumentPreviewDialog";
// import ApiServices from "../../../Utils/ApiServices/ApiServices";
// import { getDecryptedAccessToken, getDecryptedData, getDecryptedUserType } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

// const UpdatePatientDocumentsDialog = ({ open, handleClose, documents, patientData }) => {
//   const [search, setSearch] = useState("");
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [openPreview, setOpenPreview] = useState(false);
//   const [file, setFile] = useState(null);
//   const [documentType, setDocumentType] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [snackbarOpen, setSnackbarOpen] = useState(false);
//   const [snackbarMessage, setSnackbarMessage] = useState("");
//   const [snackbarSeverity, setSnackbarSeverity] = useState("info");
//   const accessToken = getDecryptedAccessToken();
//    const LoginUserDetails = getDecryptedData();
//     const LoginUserType = getDecryptedUserType();
  
//     const pid =
//       LoginUserType === "Patient" ? LoginUserDetails?.pid : patientData?.pid;

//   const filteredDocs = documents?.filter((doc) =>
//     doc.document_type.toLowerCase().includes(search.toLowerCase())
//   );

//   const handlePreview = (filePath) => {
//     const fileUrl = filePath.replace("/var/www/html", "http://18.117.9.252");
//     setSelectedFile(fileUrl);
//     setOpenPreview(true);
//   };

//   const handleUpload = async () => {
//     if (!file || !documentType) {
//       setSnackbarMessage("Please select a file and enter a document name.");
//       setSnackbarSeverity("error");
//       setSnackbarOpen(true);
//       return;
//     }

//     setLoading(true);
//     const reader = new FileReader();
//     reader.readAsDataURL(file);

//     reader.onloadend = async () => {
//       const base64File = reader.result;

//       const payload = {
//         pid: pid,
//         documentType: documentType,
//         file: base64File,
//       };

//       try {
//         const response = await ApiServices.post(
//           `/apis/default/portal/documents`,
//           payload,
//           {
//             Authorization: `Bearer ${accessToken}`,
//           }
//         );

//         if (response.data.status === "success") {
//           setSnackbarMessage("File uploaded successfully!");
//           setSnackbarSeverity("success");
//           setTimeout(handleClose, 1500); // Close dialog after success
//         } else {
//           setSnackbarMessage("Failed to upload file.");
//           setSnackbarSeverity("error");
//         }
//       } catch (error) {
//         console.error("Error uploading file:", error);
//         setSnackbarMessage("An error occurred. Please try again.");
//         setSnackbarSeverity("error");
//       } finally {
//         setLoading(false);
//         setSnackbarOpen(true);
//       }
//     };
//   };

//   return (
//     <Dialog open={open} onClose={handleClose}>
//       <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: "1px solid #ddd" }}>
//         <DialogTitle sx={{ fontWeight: "bold", color: "#028391" }}>Documents</DialogTitle>
//         <IconButton onClick={handleClose}>
//           <CloseIcon />
//         </IconButton>
//       </Box>
//       <DialogContent sx={{ p: 3 }}>
       
//         <Box sx={{ mb: 3, p: 2, border: "2px dashed #028391", borderRadius: 2 }}>
//           <Typography variant="subtitle1" sx={{ color: "#028391", mb: 2, fontWeight: "bold" }}>Upload New Document</Typography>
//           <TextField
//             fullWidth
//             variant="outlined"
//             label="Document Name"
//             value={documentType}
//             onChange={(e) => setDocumentType(e.target.value)}
//             sx={{ mb: 2 }}
//           />
//           <input
//             type="file"
//             accept="image/*,application/pdf"
//             onChange={(e) => setFile(e.target.files[0])}
//             style={{ marginBottom: "10px" }}
//           />
//           <Box display="flex" justifyContent="space-between" alignItems="center">
//             <Button
//               variant="contained"
//               sx={{ backgroundColor: "#028391", color: "#fff", "&:hover": { backgroundColor: "#026c74" } }}
//               onClick={handleUpload}
//               disabled={loading}
//             >
//               {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Upload"}
//             </Button>
//             {file && (
//               <Typography variant="body2" color="gray">
//                 Selected: {file.name}
//               </Typography>
//             )}
//           </Box>
//         </Box>

//         <TextField
//           fullWidth
//           placeholder="Search by document type..."
//           variant="outlined"
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//           InputProps={{
//             startAdornment: (
//               <InputAdornment position="start">
//                 <SearchIcon />
//               </InputAdornment>
//             ),
//           }}
//           sx={{ mb: 3 }}
//         />
//         {/* Document List */}
//         <Grid container spacing={2}>
//           {filteredDocs?.length > 0 ? (
//             filteredDocs.map((doc) => (
//               <Grid item xs={12} sm={6} md={4} key={doc.id}>
//                 <Card
//                   sx={{
//                     p: 2,
//                     display: "flex",
//                     alignItems: "center",
//                     cursor: "pointer",
//                     border: "2px solid #028391",
//                   }}
//                   onClick={() => handlePreview(doc.file_path)}
//                 >
//                   <DescriptionIcon sx={{ fontSize: 40, color: "#028391", mr: 2 }} />
//                   <CardContent>
//                     <Typography variant="subtitle1" fontWeight="bold" color="#028391">
//                       {doc.document_type}
//                     </Typography>
//                     <Typography variant="body2" color="gray">
//                       Uploaded: {new Date(doc.created_at).toLocaleDateString()}
//                     </Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))
//           ) : (
//             <Typography color="gray">No documents found.</Typography>
//           )}
//         </Grid>
//       </DialogContent>

//       {/* Document Preview Dialog */}
//       {selectedFile && (
//         <DocumentPreviewDialog open={openPreview} fileUrl={selectedFile} handleClose={() => setOpenPreview(false)} />
//       )}

//       {/* Snackbar for Notifications */}
//       <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
//         <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
//           {snackbarMessage}
//         </Alert>
//       </Snackbar>
//     </Dialog>
//   );
// };

// export default UpdatePatientDocumentsDialog;
